'use client';

import type { Address } from '@ahm/api-wrappers-core';
import { getCookie, setCookie } from 'cookies-next/client';
import { useEffect, useRef } from 'react';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { cleanupAiPlannerStorage } from '@/lib/migrations/cleanup-ai-planner';
import { useAiFoodOrderStore } from '@/store';

export default function AddressCookieSync() {
  const address = useAiFoodOrderStore.use.address();
  const setAddress = useAiFoodOrderStore.use.setAddress();
  const { showCityPopup } = useStoreCitySheet(); // Check if sheet is open
  const isWebinapp = useDetectWebInApp();
  const hasInitialized = useRef(false);
  const hasSyncedToServer = useRef(false);

  // Sync cookie to store on mount
  useEffect(() => {
    // Only run once on client hydration
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // One-shot migration — xoá localStorage/sessionStorage tồn đọng từ AI-planner
    // feature đã remove. Idempotent qua marker key.
    cleanupAiPlannerStorage();

    try {
      const addressCookie = getCookie('ahafood_address');

      if (addressCookie && !address) {
        // Cookie exists but store doesn't have address - sync them
        console.log('📥 Found address in cookie but not in store, syncing...');
        const addressObj = JSON.parse(addressCookie as string) as Address;

        // Validate the address object has required properties
        if (addressObj?.address && addressObj?.lat && addressObj?.lng) {
          setAddress(addressObj);
        }
      } else if (address && !addressCookie) {
        // Store has address but no cookie - sync to cookie and reload
        console.log('📤 Found address in store but not in cookie, syncing...');
        setCookie('ahafood_address', JSON.stringify(address), {
          maxAge: 30 * 24 * 60 * 60, // 30 days
        });

        // Only reload if StoreCityIdSheet is not open (to avoid disrupting user experience)
        if (!showCityPopup) {
          console.log('🔄 Reloading page to ensure server has address data');
          window.location.reload();
        } else {
          console.log('⏸️ Skipping page reload - StoreCityIdSheet is open');
        }
      }
    } catch (error) {
      console.error('Error syncing address cookie with store:', error);
    }
  }, [address, setAddress, showCityPopup]);

  // Sync store to cookie when address changes
  useEffect(() => {
    // Skip initial mount sync (handled above)
    if (!hasInitialized.current) return;

    // Skip if already synced to server
    if (hasSyncedToServer.current) return;

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const syncStoreToServer = () => {
      try {
        const existingCookie = getCookie('ahafood_address');

        if (address) {
          const needsSync =
            !existingCookie || JSON.stringify(address) !== existingCookie;

          if (needsSync) {
            console.log(
              '🔄 Syncing address from store to cookie for server:',
              address
            );

            setCookie('ahafood_address', JSON.stringify(address), {
              maxAge: 30 * 24 * 60 * 60,
            });

            hasSyncedToServer.current = true;

            // Conditional page reload based on mode
            if (!showCityPopup) {
              if (!isWebinapp) {
                // Native web: Reload to sync with server (SSR)
                console.log(
                  '🔄 Reloading page to sync server with new address'
                );
                setTimeout(() => window.location.reload(), 100);
              } else {
                // WebInApp: Skip reload, React Query handles refetch
                console.log(
                  '🎯 WebInApp mode: Skipping page reload, React Query will handle refetch'
                );
              }
            } else {
              console.log('⏸️ Skipping page reload - StoreCityIdSheet is open');
            }
          }
        } else if (!address && existingCookie) {
          // Address cleared from store
          console.log('🗑️ Removing address cookie as store address is null');
          setCookie('ahafood_address', '', { maxAge: -1 });

          // Conditional page reload based on mode
          if (!showCityPopup) {
            if (!isWebinapp) {
              // Native web: Reload to sync with server
              console.log(
                '🔄 Reloading page to sync server with removed address'
              );
              setTimeout(() => window.location.reload(), 100);
            } else {
              // WebInApp: Skip reload
              console.log(
                '🎯 WebInApp mode: Skipping page reload for removed address'
              );
            }
          } else {
            console.log('⏸️ Skipping page reload - StoreCityIdSheet is open');
          }
        }
      } catch (error) {
        console.error('❌ Error syncing store to server:', error);
      }
    };

    // Delay sync to avoid race conditions
    const timer = setTimeout(syncStoreToServer, 200);
    return () => clearTimeout(timer);
  }, [address, showCityPopup, isWebinapp]);

  return null;
}
